import Stack from '@mui/material/Stack'

import { Text } from '@mxenabled/mxui'
import { observer } from 'mobx-react-lite'

import { Loader, useGlobalCopyStore } from '../../../common'
import TransactionDetails from '../../../transactions/components/shared/TransactionDetails'
import type { ActionBlockProps } from '../../mappings/blocks'

import { useTransactionDetailsBlock } from './useTransactionDetailsBlock'

/**
 * Single-transaction drawer content. The detail rendering and inline editing (category picker, date,
 * memo, tags, split/hide/merchant-budget) all live in the shared `TransactionDetails` component from
 * the transactions module — this block's job is only to resolve *which* transaction and *which*
 * actions, then hand off.
 *
 * `observer` is required: the resolved transaction comes from TransactionStore, and `usePrepare` may
 * still be loading it when this first renders. Without it the drawer would stay on the loader.
 */
export const TransactionDetailsBlock = observer(function TransactionDetailsBlock({
  beat,
}: ActionBlockProps) {
  const { common: commonCopy } = useGlobalCopyStore()
  const { bottomActions, isLoading, topActions, transaction } = useTransactionDetailsBlock(beat)

  if (isLoading) return <Loader />

  // Loading finished and the guid still resolves to nothing — a stale beat pointing at a deleted or
  // out-of-window transaction. Mirrors ActionBlockLayer's unknown-block branch rather than rendering
  // an empty drawer.
  if (!transaction) {
    return (
      <Stack sx={{ p: 24 }}>
        <Text>{commonCopy.error_message}</Text>
      </Stack>
    )
  }

  return (
    <TransactionDetails
      bottomActions={bottomActions}
      topActions={topActions}
      transaction={transaction}
    />
  )
})
